import { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { CheckCircle2, Loader2, ArrowRight, Clock, Mail, Lock } from 'lucide-react';
import { trackPurchase } from '../../utils/ga4';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { api, user } = useAuth();
  const { refreshCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [isGuestPayment, setIsGuestPayment] = useState(false);

  const sessionId = searchParams.get('session_id');
  
  // Check for alternative payment data from navigation state
  const alternativePayment = location.state?.alternative_payment;
  const confirmationNumber = location.state?.confirmation_number;
  const paymentMethod = location.state?.payment_method;
  const instructions = location.state?.instructions;
  const totalAmount = location.state?.total_amount;

  useEffect(() => {
    // Handle alternative payment success
    if (alternativePayment && confirmationNumber) {
      setStatus('alternative_success');
      setPaymentInfo({
        confirmation_number: confirmationNumber,
        payment_method: paymentMethod,
        instructions: instructions,
        total_amount: totalAmount
      });
      // GA4 Ecommerce — purchase (alternative payment path)
      trackPurchase({
        transactionId: confirmationNumber,
        value: Number(totalAmount || 0),
        items: [],
      });
      refreshCart();
      return;
    }
    
    const pollPaymentStatus = async (attempts = 0) => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('pending');
        return;
      }

      try {
        const response = await api.get(`/payments/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(data);
          // Stripe expanded line_items live on data.line_items.data when present.
          const li = (data.line_items?.data || data.line_items || data.items || []);
          setLineItems(li);
          setIsGuestPayment(Boolean(data.is_guest));
          // GA4 Ecommerce — purchase (Stripe success path).
          // Idempotent via sessionStorage keyed on sessionId inside trackPurchase.
          trackPurchase({
            transactionId: sessionId,
            value: Number(data.amount_total != null ? data.amount_total / 100 : data.total_amount || 0),
            tax: Number(data.total_details?.amount_tax ? data.total_details.amount_tax / 100 : 0),
            shipping: Number(data.total_details?.amount_shipping ? data.total_details.amount_shipping / 100 : 0),
            items: li.map(it => ({
              id: it.item_id || it.price?.product || it.product_id || it.id,
              name: it.item_name || it.description || it.name,
              category: it.item_category || 'Summer Camp',
              location: it.item_category2 || it.location_name || null,
              price: Number(it.amount_total != null ? it.amount_total / 100 : it.price || 0),
              quantity: Number(it.quantity || 1),
            })),
          });
          await refreshCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, api, refreshCart, alternativePayment, confirmationNumber]);

  // Render the per-line booking summary (shared between logged-in and guest).
  const BookingSummary = () => {
    if (!lineItems.length) return null;
    // Aggregate unique what-to-bring tips so we surface them once at the end
    // of the booking summary instead of repeating per line item.
    const tips = Array.from(new Set(
      lineItems.map(it => (it.what_to_bring || '').trim()).filter(Boolean)
    ));
    return (
      <div className="bg-muted rounded-xl p-4 mb-6 text-left" data-testid="booking-summary">
        <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wider">
          Booking details
        </h3>
        <ul className="space-y-3">
          {lineItems.map((it, idx) => {
            const name = it.description || it.name || it.product_name || 'Item';
            const desc = it.price?.product_data?.description || it.product_description || '';
            const amt = Number(
              it.amount_total != null ? it.amount_total / 100
              : it.price?.unit_amount != null ? it.price.unit_amount / 100
              : it.amount || 0
            );
            return (
              <li key={idx} className="flex justify-between items-start gap-4 text-sm" data-testid={`booking-line-${idx}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{name}</p>
                  {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
                </div>
                <p className="font-semibold whitespace-nowrap">${amt.toFixed(2)}</p>
              </li>
            );
          })}
        </ul>
        {paymentInfo?.amount_total != null && (
          <div className="border-t border-border mt-3 pt-3 flex justify-between font-bold">
            <span>Total paid</span>
            <span data-testid="booking-total">
              ${(paymentInfo.amount_total / 100).toFixed(2)}
            </span>
          </div>
        )}
        {tips.length > 0 && (
          <div className="border-t border-border mt-4 pt-4" data-testid="what-to-bring">
            <h4 className="font-semibold text-sm mb-2 text-foreground flex items-center gap-1.5">
              <span aria-hidden>🎒</span> What to bring
            </h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {tips.map((t, i) => (
                <li key={i} className="leading-relaxed">{t}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Guest CTA: prompt them to set a password so they can manage bookings.
  // The fulfillment email also contains a one-click claim link, but we
  // surface the same flow inline so the parent never has to leave the
  // success page to start managing their booking.
  const GuestClaimCTA = () => (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-6 text-left" data-testid="guest-claim-cta">
      <div className="flex items-start gap-3">
        <Lock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-base mb-1">Manage your booking</h3>
          <p className="text-sm text-muted-foreground mb-3">
            We've created an account for <strong>{paymentInfo?.customer_email || ''}</strong>. Check your email
            for a link to set a password — once you do, all your bookings will appear in your dashboard
            and you can complete required forms, update emergency contacts, and add more learners.
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Mail className="h-3 w-3" /> Confirmation email is on its way (check your spam folder).
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="section-padding">
      <div className="container-app max-w-2xl">
        <Card className="card-marketing">
          <CardContent className="py-12 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-primary mx-auto mb-6 animate-spin" />
                <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment...</h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-accent" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2" data-testid="success-title">
                  Payment Successful!
                </h1>
                <p className="text-muted-foreground mb-6">
                  {isGuestPayment
                    ? "Your spot is confirmed — we've emailed your receipt and a link to claim your account."
                    : "Thank you for your purchase. You'll receive a confirmation email shortly."}
                </p>

                <BookingSummary />

                {isGuestPayment && !user && <GuestClaimCTA />}

                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2">
                  {user ? (
                    <>
                      <Link to="/parent/enrollments">
                        <Button className="btn-primary" data-testid="view-enrollments-btn">
                          View Enrollments
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/parent/forms">
                        <Button variant="outline" data-testid="complete-forms-btn">
                          Complete Required Forms
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/login">
                        <Button variant="outline" data-testid="guest-login-btn">
                          Already have an account? Sign in
                        </Button>
                      </Link>
                      <Link to="/">
                        <Button className="btn-primary" data-testid="guest-home-btn">
                          Browse more camps
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </>
            )}

            {status === 'pending' && (
              <>
                <div className="w-20 h-20 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="h-10 w-10 text-warning" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Payment Processing</h1>
                <p className="text-muted-foreground mb-8">
                  Your payment is being processed. Please check your email for confirmation.
                </p>
                <Link to="/parent">
                  <Button className="btn-primary">Go to Dashboard</Button>
                </Link>
              </>
            )}

            {status === 'alternative_success' && (
              <>
                <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-accent" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2" data-testid="alternative-success-title">
                  Registration Confirmed!
                </h1>
                <p className="text-muted-foreground mb-4">
                  Your spot has been reserved. Please complete payment within 5 business days.
                </p>
                
                {paymentInfo && (
                  <div className="bg-muted rounded-xl p-6 mb-8 text-left space-y-4">
                    <div className="flex items-center justify-between border-b border-border pb-3">
                      <span className="text-sm text-muted-foreground">Confirmation Number</span>
                      <span className="font-mono font-bold text-lg text-primary" data-testid="confirmation-number">
                        #{paymentInfo.confirmation_number}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between border-b border-border pb-3">
                      <span className="text-sm text-muted-foreground">Payment Method</span>
                      <span className="font-medium">{paymentInfo.payment_method}</span>
                    </div>
                    
                    <div className="flex items-center justify-between border-b border-border pb-3">
                      <span className="text-sm text-muted-foreground">Amount Due</span>
                      <span className="font-bold text-xl">${(Number(paymentInfo.total_amount) || 0).toFixed(2)}</span>
                    </div>
                    
                    {paymentInfo.instructions && (
                      <div className="pt-2">
                        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" />
                          {paymentInfo.instructions.title || 'Payment Instructions'}
                        </h3>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {paymentInfo.instructions.instructions?.map((instruction, idx) => (
                            <li key={idx} dangerouslySetInnerHTML={{ __html: instruction }} />
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/parent/enrollments">
                    <Button className="btn-primary" data-testid="view-enrollments-btn">
                      View Enrollments
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/parent/forms">
                    <Button variant="outline" data-testid="complete-forms-btn">
                      Complete Required Forms
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {(status === 'error' || status === 'expired') && (
              <>
                <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">!</span>
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">
                  {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
                </h1>
                <p className="text-muted-foreground mb-8">
                  {status === 'expired' 
                    ? 'Your checkout session has expired. Please try again.'
                    : 'We couldn\'t confirm your payment. Please contact support if you were charged.'
                  }
                </p>
                <Link to="/cart">
                  <Button className="btn-primary">Return to Cart</Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
